import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';

interface Branch {
  _id: string;
  name: string;
}

interface CustomerBranch {
  id: string;
  name: string;
}

interface Customer {
  _id: string;
  name: string;
  phone: string;
  email?: string;
  branches: CustomerBranch[];
}

interface CustomerSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCustomers: (phoneNumbers: string[]) => void;
  existingRecipients?: string[]; // الأرقام الموجودة مسبقاً
}

const CustomerSelectionModal: React.FC<CustomerSelectionModalProps> = ({
  open,
  onClose,
  onSelectCustomers,
  existingRecipients = []
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0); // إجمالي عدد العملاء المطابقين للفلتر
  const [preventDuplicates, setPreventDuplicates] = useState(true); // خاصية منع التكرار (مفعلة افتراضياً)
  const [allFilteredCustomerIds, setAllFilteredCustomerIds] = useState<string[]>([]); // جميع معرفات العملاء المطابقين للفلتر
  const [idsLoading, setIdsLoading] = useState(false); // حالة تحميل المعرفات

  // تحميل الفروع عند فتح النافذة
  useEffect(() => {
    if (open) {
      loadBranches();
      loadCustomers();
    }
  }, [open]);

  // تحميل العملاء عند تغيير الفلاتر
  useEffect(() => {
    if (open) {
      loadCustomers();
    }
  }, [selectedBranches, searchTerm, page]);

  const loadBranches = async () => {
    try {
      const response = await axios.get('/marketing/sms/api/branches');
      if (response.data.success) {
        setBranches(response.data.data.branches);
      }
    } catch (err) {
      console.error('خطأ في تحميل الفروع:', err);
    }
  };

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = {
        page,
        limit: 50
      };

      if (selectedBranches.length > 0) {
        params.branchIds = selectedBranches;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axios.get('/marketing/sms/api/customers', { params });
      
      if (response.data.success) {
        setCustomers(response.data.data.customers);
        setTotalPages(response.data.pages);
        setTotalFilteredCount(response.data.total); // حفظ العدد الكلي
        setAllFilteredCustomerIds([]); // إعادة تعيين المعرفات (سيتم تحميلها عند الحاجة)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل العملاء');
    } finally {
      setLoading(false);
    }
  };

  // تحميل جميع معرفات العملاء المطابقين للفلتر باستخدام endpoint خفيف
  const loadAllFilteredIds = async () => {
    if (allFilteredCustomerIds.length > 0) {
      return allFilteredCustomerIds;
    }

    setIdsLoading(true);
    try {
      const params: any = {};
      if (selectedBranches.length > 0) {
        params.branchIds = selectedBranches;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axios.get('/marketing/sms/api/customers/ids', { params });
      if (response.data.success) {
        const ids = response.data.data.ids;
        setAllFilteredCustomerIds(ids);
        return ids;
      }
      return [];
    } catch (err: any) {
      console.error('خطأ في تحميل المعرفات:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل المعرفات');
      return [];
    } finally {
      setIdsLoading(false);
    }
  };

  const handleBranchChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedBranches(typeof value === 'string' ? value.split(',') : value);
    setPage(1); // إعادة تعيين الصفحة عند تغيير الفلتر
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // إعادة تعيين الصفحة عند البحث
  };

  const handleSelectCustomer = (customerId: string) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
    } else {
      newSelected.add(customerId);
    }
    setSelectedCustomers(newSelected);
  };

  const handleSelectAll = async () => {
    // التحقق إذا كنا نريد تحديد جميع العملاء عبر جميع الصفحات
    // إذا لم يتم تحميل المعرفات بعد، قم بتحميلها باستخدام endpoint الخفيف
    if (allFilteredCustomerIds.length === 0 && totalFilteredCount > 0) {
      setIdsLoading(true);
      const allIds = await loadAllFilteredIds();
      setIdsLoading(false);
      if (allIds.length > 0) {
        setSelectedCustomers(new Set(allIds));
      }
    } else {
      // إذا كانت المعرفات محملة، قم بالتحديد/إلغاء التحديد
      if (selectedCustomers.size === allFilteredCustomerIds.length) {
        setSelectedCustomers(new Set());
      } else {
        setSelectedCustomers(new Set(allFilteredCustomerIds));
      }
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      // التحقق إذا كان جميع العملاء المختارين موجودين في الصفحة الحالية
      const selectedIds = Array.from(selectedCustomers);
      const customersInCurrentPage = customers.filter(c => selectedCustomers.has(c._id));
      
      let allSelectedCustomers: any[] = [];

      if (customersInCurrentPage.length === selectedIds.length) {
        // جميع العملاء المختارين في الصفحة الحالية - استخدمهم مباشرة
        allSelectedCustomers = customersInCurrentPage;
      } else {
        // بعض العملاء من صفحات أخرى - قم بتحميلهم باستخدام batch endpoint
        const response = await axios.post('/marketing/sms/api/customers/batch', { ids: selectedIds });
        if (response.data.success) {
          allSelectedCustomers = response.data.data.customers;
        } else {
          throw new Error('فشل في تحميل بيانات العملاء');
        }
      }

      // استخراج أرقام الهواتف
      let selectedPhones = allSelectedCustomers.map((c: any) => {
        let cleanNumber = c.phone.replace(/[\s\-\+]/g, '');
        if (cleanNumber.startsWith('05')) {
          cleanNumber = '966' + cleanNumber.substring(1);
        }
        return cleanNumber;
      });

      let finalPhones = selectedPhones;

      // تطبيق منع التكرار فقط إذا كانت الخاصية مفعلة
      if (preventDuplicates) {
        const uniquePhones = Array.from(new Set(selectedPhones));
        finalPhones = uniquePhones.filter(phone => !existingRecipients.includes(phone));
        
        if (finalPhones.length === 0) {
          setError('جميع الأرقام المختارة موجودة بالفعل في قائمة المستلمين');
          setLoading(false);
          return;
        }
        
        const ignoredCount = uniquePhones.length - finalPhones.length;
        if (ignoredCount > 0) {
          setError(`تم تجاهل ${ignoredCount} رقم مكرر`);
          setTimeout(() => setError(null), 3000);
        }
      }

      onSelectCustomers(finalPhones);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء معالجة البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCustomers(new Set());
    setSearchTerm('');
    setSelectedBranches([]);
    setPage(1);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon />
          <Typography variant="h6">اختيار عملاء من قاعدة البيانات</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* خيار منع التكرار */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Checkbox
            checked={preventDuplicates}
            onChange={(e) => setPreventDuplicates(e.target.checked)}
            color="primary"
          />
          <Typography variant="body2">
            منع إضافة الأرقام المكررة (إخفاء العملاء الموجودين بالفعل في قائمة المستلمين)
          </Typography>
          {preventDuplicates && existingRecipients.length > 0 && (
            <Chip
              label={`${existingRecipients.length} رقم موجود`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        {/* الفلاتر */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="البحث بالاسم أو رقم الهاتف..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>تصفية حسب الفرع</InputLabel>
            <Select
              multiple
              value={selectedBranches}
              onChange={handleBranchChange}
              label="تصفية حسب الفرع"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const branch = branches.find(b => b._id === value);
                    return <Chip key={value} label={branch?.name || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {branches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  <Checkbox checked={selectedBranches.indexOf(branch._id) > -1} />
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* معلومات الاختيار */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            تم اختيار {selectedCustomers.size} من {totalFilteredCount} عميل
            {idsLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={totalFilteredCount === 0 || idsLoading}
          >
            {selectedCustomers.size === customers.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* جدول العملاء */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : customers.length === 0 ? (
          <Alert severity="info">
            لا يوجد عملاء متاحين. تأكد من وجود عملاء لديهم أرقام هواتف في النظام.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        customers.length > 0 && 
                        allFilteredCustomerIds.length > 0 && 
                        selectedCustomers.size === allFilteredCustomerIds.length
                      }
                      indeterminate={
                        selectedCustomers.size > 0 && 
                        selectedCustomers.size < allFilteredCustomerIds.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>الاسم</TableCell>
                  <TableCell>رقم الهاتف</TableCell>
                  <TableCell>الفرع</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => {
                  // التحقق من وجود الرقم في القائمة الموجودة
                  let cleanNumber = customer.phone.replace(/[\s\-\+]/g, '');
                  if (cleanNumber.startsWith('05')) {
                    cleanNumber = '966' + cleanNumber.substring(1);
                  }
                  const isDuplicate = existingRecipients.includes(cleanNumber);
                  
                  return (
                    <TableRow
                      key={customer._id}
                      hover
                      onClick={() => !(isDuplicate && preventDuplicates) ? handleSelectCustomer(customer._id) : null}
                      sx={{
                        cursor: isDuplicate && preventDuplicates ? 'not-allowed' : 'pointer',
                        bgcolor: isDuplicate && preventDuplicates ? 'action.hover' : 'inherit',
                        opacity: isDuplicate && preventDuplicates ? 0.6 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCustomers.has(customer._id)}
                          disabled={isDuplicate && preventDuplicates}
                        />
                      </TableCell>
                      <TableCell>
                        {customer.name}
                        {isDuplicate && preventDuplicates && (
                          <Chip
                            label="مكرر"
                            size="small"
                            color="warning"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell dir="ltr" sx={{ textAlign: 'left' }}>
                        {customer.phone}
                      </TableCell>
                      <TableCell>
                        {customer.branches.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {customer.branches.map((branch) => (
                              <Chip
                                key={branch.id}
                                label={branch.name}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            غير محدد
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* التصفح */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              السابق
            </Button>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              صفحة {page} من {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              التالي
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          إلغاء
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedCustomers.size === 0}
          startIcon={<PersonAddIcon />}
        >
          إضافة ({selectedCustomers.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerSelectionModal;